import type { IQuestionComment } from '@quesmed/types/models';
import { BiSolidDislike, BiSolidLike, BiTrash } from 'react-icons/bi';
import { FaRegEye } from 'react-icons/fa6';
import { MdOutlineHideSource } from 'react-icons/md';
import { formatDateFromUnix } from '@/utils/date';
import { Button, ConfirmTrigger, cn } from '../../ui';
import { Description } from './Description';

type CommentProps = {
  comment: IQuestionComment[];
  className?: string;
  loading: boolean;
  onAction: (id: number, operation: string) => void;
  mode?: 'edit' | 'preview';
};

export const Comment = ({ comment, className, loading, onAction, mode = 'edit' }: CommentProps) => {
  const handleAction = (id: number, operation: string) => {
    onAction(id, operation);
  };

  return (
    <div className={cn('mt-4 space-y-3', className)}>
      {comment.map((reply) => (
        <div key={reply.id} className='rounded-lg p-3 border border-default-200 w-full'>
          <div className='flex items-start justify-between gap-3'>
            <div className='flex items-center gap-2 flex-1 min-w-0'>
              <div className='size-8 rounded-full border border-default-200 bg-default-100 flex items-center justify-center flex-shrink-0'>
                <span className='text-sm font-medium text-default-700 capitalize'>
                  {reply.user?.displayName?.charAt(0) || 'U'}
                </span>
              </div>
              <div className='flex-1 min-w-0'>
                <div className='text-sm max-w-44 md:max-w-full font-medium capitalize text-default-700 truncate'>
                  {reply.user?.displayName || 'Anonymous'}
                </div>
                <div className='text-xs text-default-500'>
                  {formatDateFromUnix(reply.createdAt as number)}
                </div>
              </div>
            </div>

            <div className='flex items-center gap-x-3'>
              <div className='flex items-center gap-2 px-2 py-2 border border-default-200 rounded-lg'>
                <div className='flex items-center gap-1'>
                  <BiSolidLike className='size-3 text-success-500' />
                  <span className='text-xs text-default-500'>{reply.likes || 0}</span>
                </div>
                <div className='flex items-center gap-1'>
                  <BiSolidDislike className='size-3 text-danger-500' />
                  <span className='text-xs text-default-500'>{reply.dislikes || 0}</span>
                </div>
              </div>
              {mode === 'edit' && (
                <ConfirmTrigger
                  type='danger'
                  title='Delete Comment'
                  isLoading={loading}
                  description={
                    <Description
                      comment={reply.comment}
                      replyCount={reply.replies?.length || 0}
                      type='delete'
                    />
                  }
                  onConfirm={() => handleAction(reply.id, 'delete')}
                  trigger={({ setIsOpen }) => (
                    <Button
                      onPress={() => setIsOpen(true)}
                      size='sm'
                      variant='faded'
                      color='danger'
                      isIconOnly
                      className=''
                    >
                      <BiTrash className='size-4' />
                    </Button>
                  )}
                />
              )}
              {mode === 'edit' && (
                <ConfirmTrigger
                  type='warn'
                  title={reply.review ? 'Unhide Comment' : 'Hide Comment'}
                  isLoading={loading}
                  description={
                    <Description
                      comment={reply.comment}
                      replyCount={reply.replies?.length || 0}
                      type={reply.review ? 'unhide' : 'hide'}
                    />
                  }
                  onConfirm={() => handleAction(reply.id, reply.review ? 'unhide' : 'hide')}
                  trigger={({ setIsOpen }) => (
                    <Button
                      onPress={() => setIsOpen(true)}
                      size='sm'
                      variant='faded'
                      color='warning'
                      isIconOnly
                      className=''
                    >
                      {reply.review ? (
                        <FaRegEye className='size-4' />
                      ) : (
                        <MdOutlineHideSource className='size-4' />
                      )}
                    </Button>
                  )}
                />
              )}
            </div>
          </div>

          {/* <div className='mt-3 text-sm ml-8 break-words text-default-700'>{reply.comment}</div> */}
          <div className='text-sm mt-3 ml-8 break-words border bg-default-100 border-default-300 rounded-lg p-2 font-mono'>
            <code className='text-default-800'>{reply.comment}</code>
          </div>
          {reply.replies && reply.replies.length > 0 && (
            <div className='mt-6 space-y-2 ml-6'>
              <div className=''>
                <h2 className='text-sm font-medium text-default-700 block -mb-2'>
                  Replies ({reply.replies.length})
                </h2>
                <Comment
                  loading={loading}
                  onAction={handleAction}
                  className='space-y-2'
                  key={reply.id}
                  comment={reply.replies}
                  mode={mode}
                />
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
